import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isEqual } from "lodash";
import useSWR from "swr";

import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider";
import { filterAscDescData, formatDate, hexToRgba } from "../../Utils.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import PawDataView from "../Common/PawDataView.js";
import { ReactComponent as EditIcon } from "../../Assets/Images/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../Assets/Images/DeleteIcon.svg";
import ApiService from "../../Api/ApiServices.js";
import Layout from "../Side bar/Layout.js";
import ImmunizationForm from "../Forms/ImmunizationsForm.js";
import { MainPageWrapper } from "./GlobalItemList";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useTheme } from "@mui/material/styles";

export default function Immunizations() {
  const { userId } = useAuth();
  const [filter, setFilter] = useState({});
  const [immunizations, setImmunizationsData] = useState([]);
  const [isShow, setIsShow] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [askConfirmation, setAskConfirmation] = useState(false);
  const [vaccinesList, setVaccinesList] = useState([]);
  const [allVaccinesList, setAllVaccinesList] = useState([]);
  const [isAdd, setIsAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const fetchIdRef = useRef(0);
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    data = [],
    mutate: fetchAPIData,
    isLoading,
  } = useSWR(
    userId ? `Immunization${userId}` : null,
    async () => {
      const { data } = await ApiService.getImmunizations(userId);
      return data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { sort } = filter;
    if (filter && filter.from > -1) {
      const allData = [...data];
      const filteredResult = filterAscDescData(allData, sort);
      setImmunizationsData(filteredResult);
    }
  }, [filter, data]);

  const getVaccines = async () => {
    try {
      const { data } = await ApiService.getVaccineslist();

      const mappedData = data.map((item) => ({
        label: item?.vaccineslist,
        value: item?.id.toString(),
        immu_dates_count: item?.immu_dates_count,
        titers_dates_count: item?.titers_dates_count,
        vaccineslist: item?.vaccineslist,
      }));
      setVaccinesList(mappedData);
      setAllVaccinesList(data);
      return mappedData;
    } catch (err) {}
  };

  useEffect(() => {
    getVaccines();
  }, []);

  const handleView = (item) => {
    setIsShow(true);
    setSelectedData(item);
    setIsAdd(false);
    setIsEdit(false);
  };

  const addOnclick = () => {
    setIsAdd(true);
    setSelectedData({});
    setIsShow(false);
    setIsEdit(false);
  };

  const handleEdit = async (item) => {
    let nameData = [];
    if (item?.vaccineslist) {
      nameData = await getVaccines();
    }
    if (nameData?.length > 0) {
      setIsEdit(true);
      setSelectedData(item);
      setIsAdd(true);
      setIsShow(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: `${t("screens.immunizations.vaccinesType")}`,
        accessor: "vaccineslist",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.immunizations.date")}`,
        accessor: "immu_date.val1",
        width: "12.5vw",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: `${t("screens.formData.view")}`,
        accessor: "view",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
              color="info"
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.info.main, 0.10)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => handleView(row.original)}
            >
               <ViewIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                  width: "24px",
                  height: "24px",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
      {
        Header: `${t("screens.formData.edit")}`,
        accessor: "edit",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
              color="info"
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.info.main, 0.10)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => handleEdit(row.original)}
            >
             <EditIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
      {
        Header: `${t("screens.formData.delete")}`,
        accessor: "delete",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
             color="info"
              sx={{
                minWidth: "40px",
                background:`${hexToRgba(theme.palette.info.main, 0.10)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => setAskConfirmation(row?.original)}
            >
              <DeleteIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);


  const props = {
    label: `${t("screens.profile.immunizations")}`,
    isLoading: isLoading,
    columns,
    columnsData: immunizations,
    details: " There are no immunizations details for this user.",
    addOnclick: addOnclick,
    fetchData,
  };

  const handleDelete = async () => {
    try {
      if (askConfirmation) {
        const { data } = await ApiService.deleteImmunizations(
          userId,
          askConfirmation
        );
        if (data?.OK) {
          fetchAPIData();
        }
      }
    } catch (error) {}
  };

  const handleDetails = () => {
    const detailArray = [
      [
        {
          name: `${t("screens.immunizations.vaccinesType")}`,
          value: selectedData?.vaccineslist,
        },
        {
          name: `${t("screens.immunizations.vaccinesTypeOther")}`,
          value: selectedData?.vaccines_other || "-",
        },
      ],
    ];

    const dateData = selectedData?.immu_date || {};

    let index = 1;
    Object.keys(dateData).forEach((key) => {
      const date = dateData[key];
      detailArray[0].push({
        name: `${t("screens.immunizations.date")} ${index}`,
        value: formatDate(date),
      });
      detailArray[0].push({
        name: `${t("screens.immunizations.information")} ${index}`,
        value: selectedData?.immu_info[key],
      });
      index += 1;
    });

    const TitersdateData = selectedData?.immu_titer1_dates || {};

    let i = 1;
    Object.keys(TitersdateData).forEach((key) => {
      const date = TitersdateData[key];
      detailArray[0].push({
        name: `${t("screens.immunizations.titersDate")} ${i}`,
        value: formatDate(date),
      });
      detailArray[0].push({
        name: `${t("screens.immunizations.titersInformation")} ${i}`,
        value: selectedData?.immu_titers_info[key],
      });
      i += 1;
    });
    return detailArray;
  };

  const viewProps = {
    label: `${t("screens.immunizations.immunizationsInformation")}`,
    askConfirmation,
    setAskConfirmation,
    onDelete: handleDelete,
    isShow,
    detailsData: handleDetails(),
  };

  return (
    <Layout>
      <MainPageWrapper>
        <PawItemList {...props} />
        <PawDataView {...viewProps} />
        {isAdd && (
          <ImmunizationForm
            setIsAdd={setIsAdd}
            isEdit={isEdit}
            data={selectedData}
            fetchAPIData={fetchAPIData}
            vaccinesList={vaccinesList}
            allVaccinesList={allVaccinesList}
          />
        )}
      </MainPageWrapper>
    </Layout>
  );
}
